import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Globe,
  Send,
  Loader2,
  Copy,
  CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import { PaisesPicker } from "@/components/PaisesPicker";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import {
  CapturaDocumentos,
  subirDocumentosCapturados,
  type DocumentoCapturado,
} from "@/components/CapturaDocumentos";
import {
  Alert,
  Button,
  Card,
  DataField,
  FormField,
  PageContainer,
  PageHeader,
  Textarea,
} from "@/components/ui";

interface CertificadoDetalle {
  id: string;
  numCertificado: string;
  fechaEmision: string;
  titularRazonSocial: string | null;
  productoNombre: string;
  productoModelo: string;
  marca: string;
}

interface Pais {
  id: string;
  codigo: string;
  nombre: string;
}

interface PaisAlcance extends Pais {
  tipo: "ORIGEN" | "PROCEDENCIA";
}

type TipoCambio = "AMPLIACION" | "REDUCCION" | "MIXTO";
type TipoPais = "ORIGEN" | "PROCEDENCIA";
type Accion = "ALTA" | "BAJA";

interface Movimiento {
  paisId: string;
  tipoPais: TipoPais;
  accion: Accion;
}

const CONTEXTO_TIPO = "SOLICITUD_TELECOM";
const TAG_DOCS = "TELECOM_AMP_PAISES";

const TIPOS_PAIS: { codigo: TipoPais; label: string }[] = [
  { codigo: "ORIGEN", label: "Origen" },
  { codigo: "PROCEDENCIA", label: "Procedencia" },
];

/** Diferencia ordenada (set-difference) entre dos listas de ids. */
function diferencia(a: string[], b: string[]): string[] {
  const setB = new Set(b);
  return a.filter((id) => !setB.has(id));
}

function mismosPaises(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

/**
 * Pantalla dedicada de Ampliación / Reducción de Países (#22), mockup
 * 22-Ampliacion_Reduccion_Paises.html. Piloto de la familia "Ampliación /
 * Reducción". Se llega desde la Selección de Certificado con ?cert=<certificadoId>.
 *
 * Captura igual al alta de Certificado Único: dos PaisesPicker (origen /
 * procedencia) precargados con el alcance vigente del certificado (DICE) y
 * editables directamente. Al enviar se calcula el delta contra el alcance
 * original (lo agregado = ALTA, lo quitado = BAJA) y se deriva el tipo de
 * movimiento; el backend (crearAmpliacionPaises) sigue recibiendo el mismo
 * contrato `movimientos[]`. La carga de documentos de soporte forma parte de la
 * captura: se seleccionan en el formulario y se suben al crear la solicitud.
 */
export function AmpliacionPaisesAltaPage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const certId = params.get("cert") ?? "";

  const [paisesOrigen, setPaisesOrigen] = useState<string[]>([]);
  const [paisesProcedencia, setPaisesProcedencia] = useState<string[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [docs, setDocs] = useState<DocumentoCapturado[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Envío en curso + solicitud creada (para reintento de subida sin re-crear).
  const [enviando, setEnviando] = useState(false);
  const [solicitudCreada, setSolicitudCreada] = useState<string | null>(null);

  const qCert = useQuery<CertificadoDetalle>({
    queryKey: ["telecom-certificado", certId],
    queryFn: async () =>
      (await api.get<CertificadoDetalle>(`/api/sector/telecom/certificados/${certId}`)).data,
    enabled: access.allowed && !!certId,
  });

  // Países en el alcance vigente del certificado (DICE).
  const qCertPaises = useQuery<PaisAlcance[]>({
    queryKey: ["telecom-certificado-paises", certId],
    queryFn: async () =>
      (await api.get<PaisAlcance[]>(`/api/sector/telecom/certificados/${certId}/paises`)).data,
    enabled: access.allowed && !!certId,
  });

  // Catálogo completo de países.
  const qPaisesCatalogo = useQuery<Pais[]>({
    queryKey: ["telecom-paises"],
    queryFn: async () => (await api.get<Pais[]>("/api/sector/telecom/paises")).data,
    enabled: access.allowed,
  });

  // Alcance vigente del certificado (base contra la que se calcula el delta).
  const baseOrigen = useMemo(
    () => (qCertPaises.data ?? []).filter((p) => p.tipo === "ORIGEN").map((p) => p.id),
    [qCertPaises.data],
  );
  const baseProcedencia = useMemo(
    () => (qCertPaises.data ?? []).filter((p) => p.tipo === "PROCEDENCIA").map((p) => p.id),
    [qCertPaises.data],
  );

  // Precargar los pickers con el alcance actual una sola vez (DEBE DECIR = DICE
  // al inicio; el usuario edita desde ahí).
  useEffect(() => {
    if (!seeded && qCertPaises.isSuccess) {
      setPaisesOrigen(baseOrigen);
      setPaisesProcedencia(baseProcedencia);
      setSeeded(true);
    }
  }, [seeded, qCertPaises.isSuccess, baseOrigen, baseProcedencia]);

  // Alcance vigente por tipo, para el bloque DICE (solo lectura).
  const alcancePorTipo = useMemo(() => {
    const m: Record<TipoPais, PaisAlcance[]> = { ORIGEN: [], PROCEDENCIA: [] };
    for (const p of qCertPaises.data ?? []) m[p.tipo].push(p);
    return m;
  }, [qCertPaises.data]);

  // Nombre por id de país para el resumen de cambios.
  const nombrePais = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of qPaisesCatalogo.data ?? []) m.set(p.id, p.nombre);
    return m;
  }, [qPaisesCatalogo.data]);

  // Delta calculado contra el alcance original → movimientos para el backend.
  const movimientos = useMemo<Movimiento[]>(() => {
    const mv: Movimiento[] = [];
    diferencia(paisesOrigen, baseOrigen).forEach((id) =>
      mv.push({ paisId: id, tipoPais: "ORIGEN", accion: "ALTA" }));
    diferencia(baseOrigen, paisesOrigen).forEach((id) =>
      mv.push({ paisId: id, tipoPais: "ORIGEN", accion: "BAJA" }));
    diferencia(paisesProcedencia, baseProcedencia).forEach((id) =>
      mv.push({ paisId: id, tipoPais: "PROCEDENCIA", accion: "ALTA" }));
    diferencia(baseProcedencia, paisesProcedencia).forEach((id) =>
      mv.push({ paisId: id, tipoPais: "PROCEDENCIA", accion: "BAJA" }));
    return mv;
  }, [paisesOrigen, paisesProcedencia, baseOrigen, baseProcedencia]);

  const hayAlta = movimientos.some((m) => m.accion === "ALTA");
  const hayBaja = movimientos.some((m) => m.accion === "BAJA");
  const tipoMovimiento: TipoCambio | null =
    hayAlta && hayBaja ? "MIXTO" : hayAlta ? "AMPLIACION" : hayBaja ? "REDUCCION" : null;
  const hayCambios = movimientos.length > 0;

  const construirBody = () => ({
    certificadoBaseId: certId,
    tipoMovimiento,
    movimientos,
    motivo: motivo.trim() || null,
  });

  const enviar = async () => {
    if (!hayCambios) {
      setError("No hay cambios en los países respecto al alcance actual del certificado.");
      return;
    }
    setError(null);
    setEnviando(true);
    try {
      let id = solicitudCreada;
      if (!id) {
        id = (
          await api.post<{ id: string }>(
            "/api/sector/telecom/solicitudes/ampliacion-paises",
            construirBody(),
          )
        ).data.id;
        setSolicitudCreada(id);
      }
      const fallidos = docs.length
        ? await subirDocumentosCapturados(docs, setDocs, CONTEXTO_TIPO, id)
        : 0;
      if (fallidos > 0) {
        setError(
          `La solicitud se registró, pero ${fallidos} documento(s) no se pudieron subir. ` +
            `Reintenta los marcados en rojo o continúa y adjúntalos desde el detalle de la solicitud.`,
        );
        setEnviando(false);
        return;
      }
      navigate(`/sector/telecom/solicitudes/${id}/success`);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo registrar la solicitud.",
      );
      setEnviando(false);
    }
  };

  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }
  if (!certId) {
    return (
      <Alert variant="warning">
        Falta el certificado. Vuelve a la{" "}
        <Link to="/telecom/servicios/seleccionar-certificado?s=ampliacion-paises" className="font-medium underline">
          selección de certificado
        </Link>.
      </Alert>
    );
  }

  const bloqueado = !!solicitudCreada;

  return (
    <PageContainer width="form">
      <PageHeader
        backTo="/telecom/servicios/seleccionar-certificado?s=ampliacion-paises"
        backLabel="Cambiar certificado"
        icon={<Globe className="h-5 w-5" />}
        title="Ampliación / Reducción de Países"
        subtitle="Edita los países (origen / procedencia) del alcance vigente; el sistema detecta automáticamente las altas y bajas."
      />

      <Alert variant="info">
        El resto del alcance (producto, normas, modelos) se conserva sin cambios.
      </Alert>

      <Card title="Certificado seleccionado">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando certificado…</div>
        ) : qCert.error || !qCert.data ? (
          <div className="text-sm text-estado-warn">No se pudo cargar el certificado seleccionado.</div>
        ) : (
          <dl className="grid gap-4 sm:grid-cols-2">
            <DataField label="Núm. de Certificado" value={<span className="font-mono">{qCert.data.numCertificado}</span>} />
            <DataField label="Razón Social" value={qCert.data.titularRazonSocial ?? "—"} />
            <DataField label="Producto" value={`${qCert.data.productoNombre} ${qCert.data.productoModelo}`} />
            <DataField label="Fecha de expedición" value={formatDate(qCert.data.fechaEmision)} />
          </dl>
        )}
      </Card>

      {/* DICE: países actuales (solo lectura) */}
      <Card title="DICE — países actuales del certificado">
        {qCertPaises.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando alcance del certificado…</div>
        ) : (qCertPaises.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-fg-subtle">El certificado no tiene países registrados en su alcance.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {TIPOS_PAIS.map((tp) => (
              <div key={tp.codigo}>
                <div className="text-xs font-semibold text-fg-muted">{tp.label}</div>
                <ul className="mt-1 space-y-1">
                  {alcancePorTipo[tp.codigo].length === 0 ? (
                    <li className="text-xs text-fg-faint">—</li>
                  ) : (
                    alcancePorTipo[tp.codigo].map((p) => (
                      <li key={p.id} className="rounded bg-surface-2 px-2 py-1 text-sm text-fg-muted">{p.nombre}</li>
                    ))
                  )}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* DEBE DECIR: pickers editables */}
      <Card title="DEBE DECIR — países del nuevo alcance">
        {qCertPaises.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando alcance del certificado…</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <PaisesPicker
              label="País(es) de origen"
              opciones={qPaisesCatalogo.data ?? []}
              seleccion={paisesOrigen}
              onChange={setPaisesOrigen}
              disabled={bloqueado}
            />
            <div className="space-y-2">
              <PaisesPicker
                label="País(es) de procedencia"
                opciones={qPaisesCatalogo.data ?? []}
                seleccion={paisesProcedencia}
                onChange={setPaisesProcedencia}
                disabled={bloqueado}
              />
              {!bloqueado && paisesOrigen.length > 0 && !mismosPaises(paisesOrigen, paisesProcedencia) && (
                <button
                  type="button"
                  onClick={() => setPaisesProcedencia(paisesOrigen)}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-primary-strong bg-primary-tint px-3 py-2 text-sm font-medium text-primary-strong transition hover:border-solid hover:bg-primary/10"
                >
                  <Copy className="h-4 w-4" />
                  Usar los mismos países de origen
                  <span className="rounded-full bg-surface px-1.5 py-0.5 text-[10px] font-semibold">
                    {paisesOrigen.length}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Resumen de cambios detectados */}
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            Cambios detectados{hayCambios ? ` (${movimientos.length})` : ""}
          </div>
          {!hayCambios ? (
            <p className="mt-1 text-sm text-fg-subtle">
              Aún no hay cambios respecto al alcance actual. Agrega o quita países arriba.
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {movimientos.map((m) => (
                <span
                  key={`${m.tipoPais}-${m.accion}-${m.paisId}`}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    m.accion === "ALTA" ? "bg-estado-ok-bg text-estado-ok" : "bg-estado-err-bg text-estado-err"
                  }`}
                >
                  {m.accion === "ALTA" ? "+" : "−"} {nombrePais.get(m.paisId) ?? m.paisId}
                  <span className="opacity-70">
                    · {m.tipoPais === "ORIGEN" ? "origen" : "procedencia"}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>

        <FormField label="Justificación / comentarios" className="mt-4">
          <Textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            disabled={bloqueado}
            rows={3}
            placeholder="Explica el motivo del cambio de países (opcional)."
          />
        </FormField>
      </Card>

      {/* Documentos de soporte — parte de la captura */}
      <CapturaDocumentos
        tag={TAG_DOCS}
        titulo="Documentos de soporte"
        ayuda="Adjunta los documentos del trámite. Se subirán junto con la solicitud al enviarla."
        docs={docs}
        onChange={setDocs}
        disabled={enviando}
      />

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Link to="/telecom/servicios/seleccionar-certificado?s=ampliacion-paises">
          <Button variant="secondary" className="w-full sm:w-auto">Regresar</Button>
        </Link>
        {bloqueado ? (
          <>
            <Button
              variant="secondary"
              loading={enviando}
              onClick={enviar}
              className="w-full sm:w-auto"
            >
              Reintentar documentos
            </Button>
            <Button
              variant="success"
              icon={<CheckCircle2 className="h-4 w-4" />}
              onClick={() => navigate(`/sector/telecom/solicitudes/${solicitudCreada}/success`)}
              className="w-full sm:w-auto"
            >
              Continuar
            </Button>
          </>
        ) : (
          <Button
            variant="success"
            icon={<Send className="h-4 w-4" />}
            loading={enviando}
            disabled={!hayCambios || qCert.isLoading || !!qCert.error}
            onClick={enviar}
            className="w-full sm:w-auto"
          >
            Enviar solicitud
          </Button>
        )}
      </div>
    </PageContainer>
  );
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return s;
  }
}
